import { signedFetch } from './signedFetch';

const API_BASE = '/api';

export interface MissingMetric {
  name: string;
  priority: string;
  tag: string;
  silent?: string;
  what?: string;
  why?: string;
  current?: string;
  where?: string;
  implement?: string;
  impact?: string;
  section?: string; // 'service' | 'dependency' | 'auto-rollback'
  type?: string; // 'individual' | 'derived'
  crLink?: string;
}

export interface MetricsResponse {
  pipeline: string;
  metrics: MissingMetric[];
  feedback: Record<string, string>;
  autoRollbackAnalysis?: any;
}

export async function listPipelines(): Promise<string[]> {
  const res = await fetch(`${API_BASE}/pipelines`);
  const data = await res.json();
  return data.pipelines || [];
}

export async function getMetrics(pipeline: string): Promise<MetricsResponse> {
  const res = await fetch(`${API_BASE}/metrics/${encodeURIComponent(pipeline)}`);
  const data = await res.json();
  // Fetch live feedback from API (SigV4 signed)
  let feedback: Record<string, string> = {};
  try {
    const fbRes = await signedFetch(`https://gamoy9u6n9.execute-api.us-west-2.amazonaws.com/feedback/${encodeURIComponent(pipeline)}`);
    if (fbRes.ok) feedback = await fbRes.json();
  } catch {}
  return {
    pipeline: data.pipeline,
    metrics: (() => {
      const parsed = typeof data.metricsJson === 'string' ? JSON.parse(data.metricsJson) : (data.metrics || []);
      // Handle both flat array and {metrics: [...], autoRollbackAnalysis: {...}} formats
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.metrics)) return parsed.metrics;
      return [];
    })(),
    autoRollbackAnalysis: (() => {
      const parsed = typeof data.metricsJson === 'string' ? JSON.parse(data.metricsJson) : data;
      if (parsed && parsed.autoRollbackAnalysis) return parsed.autoRollbackAnalysis;
      return null;
    })(),
    feedback,
  };
}

export async function submitFeedback(pipeline: string, metricName: string, helpful: string): Promise<void> {
  await signedFetch(`https://gamoy9u6n9.execute-api.us-west-2.amazonaws.com/feedback/${encodeURIComponent(pipeline)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ metricName, helpful }),
  });
}

const REQUESTS_API = 'https://gamoy9u6n9.execute-api.us-west-2.amazonaws.com/requests';

export interface AnalysisRequest {
  pipeline: string;
  requestedBy: string;
  status: string;
  requestedAt: string;
}

export async function submitAnalysisRequest(pipeline: string, requestedBy: string): Promise<void> {
  await signedFetch(REQUESTS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline, requestedBy }),
  });
}

export async function getAnalysisRequests(): Promise<AnalysisRequest[]> {
  try {
    const res = await signedFetch(REQUESTS_API);
    if (!res.ok) return [];
    return await res.json();
  } catch { return []; }
}

export async function updateRequestStatus(pipeline: string, requestedAt: string, status: string): Promise<void> {
  await signedFetch(REQUESTS_API, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline, requestedAt, status }),
  });
}

export async function deleteAnalysisRequest(pipeline: string, requestedAt: string): Promise<void> {
  await signedFetch(REQUESTS_API, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline, requestedAt }),
  });
}
